import { normalizeCategory } from "./campaignCategories";
import { parseAmounts } from "./campaignPayment";
import {
  CampaignFormValues,
  CollectionMode,
  formatKr,
  progressLabel,
  progressPercent,
} from "./campaignKind";

/** Eksempeltall i forhåndsvisningen (nye kampanjer har ingen gaver ennå). */
export const SAMPLE_RAISED_ORE = 3_800_000;
export const SAMPLE_DONORS = 24;
/** Mål i eksempelet når kampanjen ikke har et eget mål. */
export const SAMPLE_TARGET_KR = 150_000;

/** Samme standardforslag som giverskjermen i appen. */
export const DEFAULT_SUGGESTIONS = [100, 200, 500];
const MAX_SUGGESTIONS = 6;

export interface PreviewProgress {
  raisedOre: number;
  targetKr: number;
  donorCount: number;
  percent: number;
  /** «38 000 av 150 000 kr · 24 givere» */
  label: string;
  /** «38 000 kr» */
  raisedLabel: string;
  /** «av 150 000 kr innsamlet» */
  ofLabel: string;
  /** true = eksempeltall, ikke ekte gaver. */
  sample: boolean;
}

export interface CampaignPreviewModel {
  mode: CollectionMode;
  name: string;
  description: string;
  /** Hva som står til høyre på kortet i appen. */
  trailing: "give" | "open" | "info";
  progress: PreviewProgress | null;
  /** Mål i kroner, 0 = uten mål. */
  targetKr: number;
  linkHost: string | null;
  intervals: Array<"ONE_TIME" | "MONTHLY">;
  amounts: number[];
  providers: Array<"VIPPS" | "STRIPE">;
  /** Kategoriknappene på giverskjermen (tom = ingen valg). */
  categories: string[];
}

/** «https://www.qr.vipps.no/…» → «qr.vipps.no». Ugyldig lenke gir null. */
export const linkHost = (link: string | null | undefined) => {
  if (!link || !link.trim()) return null;
  try {
    const url = new URL(link.trim());
    if (!/^https?:$/.test(url.protocol) || !url.hostname) return null;
    return url.hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
};

/** Forslagene giveren ser: gyldige verdier (maks 6), ellers 100, 200, 500. */
export const previewAmounts = (text: string) => {
  const parsed = parseAmounts(text) ?? [];
  return parsed.length ? parsed.slice(0, MAX_SUGGESTIONS) : DEFAULT_SUGGESTIONS;
};

/**
 * Fremdriften i forhåndsvisningen. Vises bare for gaver i appen med
 * «Vis fremdrift i appen» på. Ekte tall brukes når kampanjen har dem,
 * ellers eksempeltall (38 000 kr, 24 givere) mot kampanjens eget mål.
 */
export const previewProgress = (
  values: Pick<
    CampaignFormValues,
    "collectionMode" | "showProgress" | "targetAmount"
  >,
  real?: { raisedOre?: number | null; donorCount?: number | null } | null,
): PreviewProgress | null => {
  if (values.collectionMode !== "IN_APP" || !values.showProgress) return null;
  const target = Number(values.targetAmount) || 0;
  const sample = real?.raisedOre == null;
  const raisedOre = sample ? SAMPLE_RAISED_ORE : (real!.raisedOre as number);
  const donorCount = sample ? SAMPLE_DONORS : (real?.donorCount ?? 0);
  const targetKr = target > 0 ? target : sample ? SAMPLE_TARGET_KR : 0;
  return {
    raisedOre,
    targetKr,
    donorCount,
    percent: progressPercent(raisedOre, targetKr),
    label: progressLabel(raisedOre, targetKr, donorCount),
    raisedLabel: formatKr(raisedOre / 100),
    ofLabel: targetKr > 0 ? `av ${formatKr(targetKr)} innsamlet` : "innsamlet",
    sample,
  };
};

/** Alt forhåndsvisningen trenger, regnet ut fra skjemaet. */
export const campaignPreview = (
  values: CampaignFormValues,
  real?: { raisedOre?: number | null; donorCount?: number | null } | null,
): CampaignPreviewModel => {
  const mode: CollectionMode = values.collectionMode || "INFO";
  const providers: Array<"VIPPS" | "STRIPE"> = [];
  if (mode === "IN_APP" && values.vippsEnabled) providers.push("VIPPS");
  if (mode === "IN_APP" && values.stripeEnabled) providers.push("STRIPE");
  const intervals: Array<"ONE_TIME" | "MONTHLY"> = [];
  if (values.oneTimeEnabled) intervals.push("ONE_TIME");
  if (values.monthlyEnabled) intervals.push("MONTHLY");
  const host = mode === "LINK" ? linkHost(values.link) : null;
  return {
    mode,
    name: values.name.trim() || "Navn på kampanjen",
    description:
      values.description.trim() || "Kort beskrivelse av kampanjen vises her.",
    trailing:
      mode === "IN_APP" && providers.length
        ? "give"
        : mode === "LINK" && host
          ? "open"
          : "info",
    progress: previewProgress(values, real),
    targetKr: Number(values.targetAmount) || 0,
    linkHost: host,
    intervals: intervals.length ? intervals : ["ONE_TIME"],
    amounts: previewAmounts(values.suggestedAmounts),
    providers,
    categories:
      mode === "IN_APP"
        ? (values.categories ?? []).map(normalizeCategory).filter(Boolean)
        : [],
  };
};

/** Lengste tekst på betalingsknappen før kategorien droppes. */
export const MAX_PAY_LABEL = 34;

/**
 * Teksten på betalingsknappen. Uten kategori: «Gi med Vipps» / «Betal med kort».
 * Med valgt kategori: «Gi 250 kr til Zakat med Vipps», eller «Gi 250 kr med
 * Vipps» hvis det blir for langt.
 */
export const payLabel = (
  provider: "VIPPS" | "STRIPE",
  amountKr: number,
  category?: string | null,
) => {
  const via = provider === "VIPPS" ? "Vipps" : "kort";
  if (!category)
    return provider === "VIPPS" ? "Gi med Vipps" : "Betal med kort";
  const kr = formatKr(amountKr);
  const full = `Gi ${kr} til ${category} med ${via}`;
  return full.length <= MAX_PAY_LABEL ? full : `Gi ${kr} med ${via}`;
};
